// CadProjectsPage — стартовый экран Кубикон CAD.
//
// Две вкладки:
//   «Мои проекты»  — все мои (приватные + публичные) через listUserProjects
//   «Лента»         — публичные проекты всех пользователей с поиском/фильтрами
//
// Открывается на:
//   /main/cad-editor       → вкладка «Мои»  (initialTab='my')
//   /main/cad-projects     → вкладка «Лента» (initialTab='feed')
//
// CSS — свой тёмный модуль CadProjects.module.css на токенах дизайн-системы.

import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    listUserProjects, getFeed, deleteProject as apiDeleteProject,
} from '../../API/KubikonCadService';
import { getCurrentUserId } from './cloudIO';
// Свой тёмный модуль на токенах дизайн-системы. Раньше брали стили у
// SchemaPicker робототехники — оттуда белые карточки на тёмном фоне.
import cl from './CadProjects.module.css';
import { IconPlus, IconModel, IconGlobe, IconHeart, IconEye, IconTrash, IconComment } from './projectIcons';


function formatDate(s) {
    if (!s) return '';
    try {
        const d = new Date(s);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString('ru', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (_) { return ''; }
}


// === Превью проекта (base64 PNG из thumbnail) ===
const ProjectPreview = ({ thumbnail, width = 260, height = 140 }) => {
    if (thumbnail && thumbnail.startsWith('data:image')) {
        return (
            <img
                src={thumbnail}
                alt="превью"
                style={{ width, height, objectFit: 'cover', display: 'block' }}
            />
        );
    }
    // Заглушка для проекта без превью. Цвета — из дизайн-системы: раньше здесь
    // был светлый прямоугольник, который на тёмной странице выглядел заплаткой.
    return (
        <div style={{
            width: '100%', height: '100%',
            background: 'var(--k-surface-3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--k-ink-faint)',
        }}>
            <IconModel size={44} />
        </div>
    );
};


// === Вкладка «Мои проекты» ===
const MyTab = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const uid = getCurrentUserId();
                if (!uid) {
                    if (!cancelled) { setItems([]); setLoading(false); }
                    return;
                }
                const res = await listUserProjects(uid);
                if (cancelled) return;
                setItems(res.data?.items || []);
            } catch (e) {
                console.error('[CadProjectsPage] listUserProjects failed:', e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const onDelete = async (id, e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!window.confirm('Удалить этот проект? Действие необратимо.')) return;
        setDeletingId(id);
        try {
            const uid = getCurrentUserId();
            await apiDeleteProject(id, uid);
            setItems((prev) => prev.filter((p) => p.id !== id));
        } catch (e) {
            alert('Не удалось удалить: ' + (e?.message || e));
        } finally {
            setDeletingId(null);
        }
    };

    const onCreate = () => navigate('/main/cad-editor/new');

    return (
        <div className={cl.grid}>
            <button
                type="button"
                onClick={onCreate}
                className={`${cl.card} ${cl.cardNew} ${cl.cardNewBtn}`}
            >
                <div className={cl.cardNewIcon}><IconPlus size={30} /></div>
                <div className={cl.cardNewTitle}>Создать новый проект</div>
                <div className={cl.cardNewSubtitle}>Пустой 3D-документ</div>
            </button>

            {loading && <div className={cl.loadingCard}>Загружаем твои проекты...</div>}
            {!loading && items.length === 0 && (
                <div className={cl.emptyCard}>У тебя пока нет проектов. Создай первый!</div>
            )}

            {items.map((p) => (
                <Link
                    key={p.id}
                    to={`/main/cad-editor/${p.id}`}
                    className={cl.card}
                >
                    <div className={cl.cardPreview}>
                        <ProjectPreview thumbnail={p.thumbnail}/>
                    </div>
                    <div className={cl.cardBody}>
                        <h3 className={cl.cardTitle}>{p.title || 'Без названия'}</h3>
                        {p.description && <p className={cl.cardDesc}>{p.description}</p>}
                        <div className={cl.cardMeta}>
                            <span className={cl.cardType}><IconModel size={15} /> CAD</span>
                            <span className={cl.cardDate}>{formatDate(p.updated_at)}</span>
                        </div>
                        {p.is_public && <div className={cl.cardPublic}><IconGlobe size={13} /> В ленте</div>}
                        {(p.likes_count > 0 || p.views_count > 0 || p.comments_count > 0) && (
                            <div className={cl.cardStats}>
                                {p.likes_count > 0 && <span><IconHeart size={13} /> {p.likes_count}</span>}
                                {p.views_count > 0 && <span><IconEye size={13} /> {p.views_count}</span>}
                                {p.comments_count > 0 && <span><IconComment size={13} /> {p.comments_count}</span>}
                            </div>
                        )}
                    </div>
                    <button
                        className={cl.deleteBtn}
                        onClick={(e) => onDelete(p.id, e)}
                        disabled={deletingId === p.id}
                        title="Удалить"
                    >
                        {deletingId === p.id ? '...' : <IconTrash size={15} />}
                    </button>
                </Link>
            ))}
        </div>
    );
};


// === Вкладка «Лента» ===
const FeedTab = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState('');
    const [debouncedQ, setDebouncedQ] = useState('');
    const [sort, setSort] = useState('fresh');
    const [offset, setOffset] = useState(0);
    const PAGE = 24;

    useEffect(() => {
        const id = setTimeout(() => setDebouncedQ(q.trim()), 400);
        return () => clearTimeout(id);
    }, [q]);

    useEffect(() => { setOffset(0); setItems([]); }, [debouncedQ, sort]);

    const load = useCallback(async (curOffset) => {
        setLoading(true);
        try {
            const res = await getFeed({
                q: debouncedQ, sort, limit: PAGE, offset: curOffset,
            });
            const data = res.data?.items || [];
            setTotal(res.data?.total || 0);
            if (curOffset === 0) setItems(data);
            else setItems((prev) => [...prev, ...data]);
        } catch (e) {
            console.error('[CadProjectsPage] getFeed failed:', e);
        } finally {
            setLoading(false);
        }
    }, [debouncedQ, sort]);

    useEffect(() => { load(0); }, [load]);

    const hasMore = items.length < total;
    const loadMore = () => {
        const newOffset = offset + PAGE;
        setOffset(newOffset);
        load(newOffset);
    };

    return (
        <div>
            <div className={cl.feedToolbar}>
                <input
                    type="text"
                    className={cl.searchInput}
                    placeholder="🔍 Поиск проектов по названию или описанию..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
                <div className={cl.filterGroup}>
                    <label className={cl.filterLabel}>Сортировка:</label>
                    <select value={sort} onChange={(e) => setSort(e.target.value)} className={cl.filterSelect}>
                        <option value="fresh">Новые</option>
                        <option value="popular">Популярные</option>
                        <option value="views">По просмотрам</option>
                    </select>
                </div>
            </div>

            {total > 0 && (
                <div className={cl.feedTotal}>Найдено проектов: {total}</div>
            )}

            <div className={cl.grid}>
                {items.length === 0 && !loading && (
                    <div className={cl.emptyCard}>
                        {debouncedQ
                            ? `По запросу «${debouncedQ}» ничего не найдено`
                            : 'В ленте пока нет проектов. Будь первым — опубликуй свой!'}
                    </div>
                )}

                {items.map((p) => (
                    <Link
                        key={p.id}
                        to={`/main/cad-projects/${p.id}`}
                        className={cl.card}
                    >
                        <div className={cl.cardPreview}>
                            <ProjectPreview thumbnail={p.thumbnail}/>
                        </div>
                        <div className={cl.cardBody}>
                            <h3 className={cl.cardTitle}>{p.title || 'Без названия'}</h3>
                            {p.description && <p className={cl.cardDesc}>{p.description}</p>}
                            <div className={cl.cardMeta}>
                                <span className={cl.cardType}><IconModel size={15} /> CAD</span>
                                <span className={cl.cardDate}>{formatDate(p.updated_at)}</span>
                            </div>
                            <div className={cl.cardStats}>
                                <span><IconHeart size={13} /> {p.likes_count || 0}</span>
                                <span><IconEye size={13} /> {p.views_count || 0}</span>
                                <span><IconComment size={13} /> {p.comments_count || 0}</span>
                                {p.forked_from_id && <span title="Это копия чужого проекта">🍴</span>}
                            </div>
                        </div>
                    </Link>
                ))}
            </div>

            {loading && <div className={cl.loadingCard}>Загружаем...</div>}
            {hasMore && !loading && (
                <button className={cl.loadMoreBtn} onClick={loadMore}>
                    Показать ещё
                </button>
            )}
        </div>
    );
};


// === Главный компонент ===
const CadProjectsPage = ({ initialTab = 'my' }) => {
    const [tab, setTab] = useState(initialTab);

    return (
        <div className={cl.page}>
            <div className={cl.header}>
                <h1 className={cl.title}>Кубикон CAD</h1>
                <div className={cl.tabs}>
                    <button
                        className={`${cl.tab} ${tab === 'my' ? cl.tabActive : ''}`}
                        onClick={() => setTab('my')}
                    >
                        📁 Мои проекты
                    </button>
                    <button
                        className={`${cl.tab} ${tab === 'feed' ? cl.tabActive : ''}`}
                        onClick={() => setTab('feed')}
                    >
                        <IconGlobe size={15} /> Лента сообщества
                    </button>
                </div>
            </div>
            {tab === 'my' ? <MyTab/> : <FeedTab/>}
        </div>
    );
};

export default CadProjectsPage;
